import type { ClassKind, ClassLevel } from "./enums.ts";

/**
 * Class types, levels and duets (Studio S2). Pure, so the API, Desk and the phone read one rule.
 *
 * A private class holds one person. A duet is a private class for two: `kind` stays `private` and
 * the places are 2. A private package pays for a duet place like a private place: one private
 * session for each person who books.
 */

/** The most places a private class can have: two, a duet. */
export const MAX_PRIVATE_SEATS = 2;

/**
 * The places of a private class or session from a request: 2 for a duet, else 1. Any other value
 * (an older client that sends the places of a group, or none) is one place, the rule before duets.
 */
export function privateSeats(requested: number | null | undefined): 1 | 2 {
  return requested === MAX_PRIVATE_SEATS ? 2 : 1;
}

/** A private class or session with two places. */
export function isDuet(kind: ClassKind, capacity: number | null | undefined): boolean {
  return kind === "private" && capacity === MAX_PRIVATE_SEATS;
}

/** The words of each level, for the badge and the class panel. */
export const classLevelWords: Record<ClassLevel, string> = {
  all_levels: "All levels",
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

/** The word of a level, or null when the class has none (no badge). */
export function classLevelWord(level: ClassLevel | null | undefined): string | null {
  return level ? classLevelWords[level] : null;
}

/**
 * The places of a session as a member reads them on a card. A duet says how many of its two places
 * are open; a full one says so. Null for any other session: the card keeps its own words.
 */
export function duetPlacesText(session: { kind: ClassKind; capacity: number | null; spotsLeft: number | null }): string | null {
  if (!isDuet(session.kind, session.capacity)) return null;
  const left = session.spotsLeft ?? 0;
  if (left <= 0) return "Duet, both places taken";
  return left === 1 ? "Duet, 1 of 2 places left" : "Duet, 2 places";
}
